/** Connector types, and discovery reads (each off until what it needs is
 *  picked). Discovery is read live, so it is kept only briefly. */

import { useQuery } from "@tanstack/react-query";
import { api, getProjectSlug } from "@/lib/api";
import { useApiQuery } from "@/lib/query";
import type { AgentSpec } from "@/features/agents/types";
import type {
  Connector,
  RemoteGroup,
  RemoteTarget,
  RemoteVersion,
  RequestPreview,
  RequestPreviewInput,
} from "./types";

const FRESH = { staleTime: 30_000 };
const enc = encodeURIComponent;
const targetsPath = (c: string | null, g: string | null) =>
  `/connections/${c}/groups/${enc(g ?? "")}/targets`;

export const useConnectors = (enabled = true) => useApiQuery<Connector[]>("/connectors", {}, { enabled });

export const useGroups = (connectionId: string | null) =>
  useApiQuery<RemoteGroup[]>(
    `/connections/${connectionId}/groups`,
    {},
    { enabled: !!connectionId, ...FRESH },
  );

export const useTargets = (connectionId: string | null, group: string | null) =>
  useApiQuery<RemoteTarget[]>(
    targetsPath(connectionId, group),
    // Connections are shared; agent ids are per project, so say which one.
    { project: getProjectSlug() },
    { enabled: !!connectionId && !!group, ...FRESH },
  );

export const useVersions = (connectionId: string | null, group: string | null, target: string | null) =>
  useApiQuery<RemoteVersion[]>(
    `${targetsPath(connectionId, group)}/${enc(target ?? "")}/versions`,
    {},
    { enabled: !!connectionId && !!group && !!target, ...FRESH },
  );

export const useDiscoveredSpec = (
  connectionId: string | null,
  group: string | null,
  target: string | null,
  version: string,
) =>
  useApiQuery<AgentSpec>(
    `${targetsPath(connectionId, group)}/${enc(target ?? "")}/versions/${enc(version)}/spec`,
    {},
    { enabled: !!connectionId && !!group && !!target, ...FRESH },
  );

/** The request the connector would send for one item (secrets masked). Pure on
 *  the server, so it is a read keyed by everything that shapes it. */
export const useRequestPreview = (connectorType: string | null, input: RequestPreviewInput | null) =>
  useQuery({
    queryKey: ["/connectors/request-preview", connectorType, input],
    queryFn: () => api.post<RequestPreview>(`/connectors/${enc(connectorType ?? "")}/request-preview`, input),
    enabled: !!connectorType && !!input,
  });
